import { Spacing, Typography } from '@persona-id/design-tokens';
import { keyBy } from 'lodash/fp';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import styled from 'styled-components';

import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import TextAreaField from 'dashboard/components/FormControls/TextAreaField';
import { OptionType } from 'dashboard/components/ReactSelect';
import TruncatedText from 'dashboard/components/TruncatedText';
import FormAssignCaseOption from 'dashboard/forms/FormAssignCase/FormAssignCaseOption';
import FormAssignCaseSingleValue from 'dashboard/forms/FormAssignCase/FormAssignCaseSingleValue';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { User, UserAvailability, UserStatus } from 'dashboard/models/user';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';

const Section = styled('div')`
  margin-bottom: ${Spacing[5]};
`;

const Label = styled('h4')`
  margin-bottom: ${Spacing[2]};
`;

const HighlightText = styled(TruncatedText)`
  ${Typography.TextMediumBold};
`;

const StyledParagraph = styled(Paragraph)`
  position: relative;
  top: -${Spacing[4]};
`;

interface FormAssignCaseProps {
  handleCancel: () => void;
  users: User[];
}

export interface FormAssignCaseUserOption extends OptionType {
  label: string;
  availability: UserAvailability;
  isCurrentUser: boolean;
}

const FormAssignCase = ({
  handleCancel,
  handleSubmit,
  users,
  submitting,
}: FormAssignCaseProps & InjectedFormProps<unknown, FormAssignCaseProps>) => {
  const [selectedUserName, setSelectedUserName] = useState<string>('');
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [selectedUserAvailability, setSelectedUserAvailability] = useState<UserAvailability>(null);
  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const otherUsers: FormAssignCaseUserOption[] = users
    .filter(
      (user: User) => user.attributes.status === UserStatus.Active && user.id !== currentUser.id,
    )
    .map((user: User) => {
      return {
        label: userDisplayName(user),
        value: user.id,
        availability: user.attributes.availability,
        isCurrentUser: false,
      };
    });

  // We always want "Assign to me" to be the first visible option, so we check for it separately
  const currentUserFullName = [
    currentUser.attributes['name-first'],
    currentUser.attributes['name-last'],
  ]
    .filter(Boolean)
    .join(' ');

  const myUser = users.some((user: User) => user.id === currentUser.id)
    ? [
        {
          label: `${currentUserFullName}`,
          value: currentUser.id,
          availability: currentUser.attributes.availability,
          isCurrentUser: true,
        },
      ]
    : [];

  const unassigned: FormAssignCaseUserOption[] = [
    {
      label: 'Unassigned',
      value: '',
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      availability: null as UserAvailability,
      isCurrentUser: false,
    },
  ];

  const options = [...myUser, ...unassigned, ...otherUsers];

  const usersMap = keyBy('value', options);

  const handleChangeSelectedUserId = (userId: string) => {
    setSelectedUserName(usersMap[userId].label);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setSelectedUserAvailability(usersMap[userId].availability);
  };

  // TODO: (melissa) convert to redux-hook-form
  return (
    <form onSubmit={handleSubmit}>
      <Section>
        <Label>Assign to</Label>
        <Field
          component={ReactSelectField}
          components={{
            Option: FormAssignCaseOption,
            SingleValue: FormAssignCaseSingleValue,
          }}
          name='userId'
          id='userId'
          placeholder='Unassigned'
          disabled={submitting}
          options={options}
          isSearchable={true}
          isClearable={true}
          portal={true}
          onChange={handleChangeSelectedUserId as any}
        />
        {selectedUserAvailability === UserAvailability.Unavailable && (
          <StyledParagraph color='primary'>
            <HighlightText>{selectedUserName}</HighlightText> is{' '}
            <HighlightText>unavailable</HighlightText>. If this case requires immediate attention,
            please re-assign to a different agent.
          </StyledParagraph>
        )}

        <Label>Comment (optional)</Label>
        <Field component={TextAreaField} name='message' id='message' disabled={submitting} />
      </Section>

      <HStack equal={true}>
        <Button
          id='form-assign-case__cancel'
          data-test='form-assign-case__cancel'
          disabled={submitting}
          variant='basic'
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          data-test='form-assign-case__submit'
          disabled={submitting}
          isPending={submitting}
        >
          Assign
        </Button>
      </HStack>
    </form>
  );
};

export interface AssignCaseFormData {
  userId: string;
  message: string;
}

export default reduxForm<AssignCaseFormData, FormAssignCaseProps>({
  form: 'assignCase',
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
})(FormAssignCase);
